const DEFAULT_MAX_CONCURRENCY = 2
const IDLE_POLL_MS = 50

export function createTranscriptChunkQueue({
	maxConcurrency = DEFAULT_MAX_CONCURRENCY,
	transcribe,
	onJobStart,
	onJobSuccess,
	onJobError,
} = {}) {
	if (typeof transcribe !== 'function') {
		throw new Error('transcribe function is required')
	}

	let queue = []
	let inFlight = 0
	let disposed = false
	let abortController = new AbortController()

	const processJob = job => {
		Promise.resolve()
			.then(async () => {
				if (disposed) return

				onJobStart?.(job)

				const result = await transcribe(job, abortController.signal)
				if (disposed) return

				onJobSuccess?.(job, result)
			})
			.catch(error => {
				if (disposed) return
				if (error?.name === 'AbortError') return

				onJobError?.(job, error)
			})
			.finally(() => {
				inFlight -= 1
				pump()
			})
	}

	const pump = () => {
		if (disposed) return

		while (inFlight < maxConcurrency && queue.length > 0) {
			const job = queue.shift()
			inFlight += 1
			processJob(job)
		}
	}

	const enqueue = job => {
		if (disposed) return
		queue.push(job)
		pump()
	}

	/** Drop pending jobs and enqueue only the latest (for rolling full-blob STT). */
	const enqueueLatest = job => {
		if (disposed) return
		queue = [job]
		pump()
	}

	const flush = ({ timeoutMs = 30000 } = {}) =>
		new Promise((resolve, reject) => {
			if (disposed && queue.length === 0 && inFlight === 0) {
				resolve()
				return
			}

			const startedAt = Date.now()

			const check = () => {
				if (queue.length === 0 && inFlight === 0) {
					resolve()
					return
				}

				if (Date.now() - startedAt >= timeoutMs) {
					const error = new Error('Transcript drain timed out')
					error.name = 'TranscriptDrainTimeout'
					reject(error)
					return
				}

				window.setTimeout(check, IDLE_POLL_MS)
			}

			check()
		})

	const reset = () => {
		disposed = false
		queue = []
		inFlight = 0
		abortController = new AbortController()
	}

	const dispose = () => {
		disposed = true
		abortController.abort()
		queue = []
	}

	const getPendingCount = () => queue.length + inFlight

	return {
		enqueue,
		enqueueLatest,
		flush,
		reset,
		dispose,
		getPendingCount,
	}
}
